import { useState, useEffect, useRef } from 'react'
import { useAuthStore } from './useAuthStore'

export function AuthPage({ onDone, onBack }: { onDone: () => void; onBack?: () => void }) {
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [accountType, setAccountType] = useState<'individual' | 'enterprise'>('individual')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [ssoMode, setSsoMode] = useState(false)
  const [ssoEmail, setSsoEmail] = useState('')
  const [ssoError, setSsoError] = useState('')
  const { login, register } = useAuthStore()
  const containerRef = useRef<HTMLDivElement>(null)

  const API = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3001'

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    const userStr = params.get('user')
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr)
        useAuthStore.setState({ token, user })
        window.history.replaceState({}, '', window.location.pathname)
        onDone()
      } catch {}
    }
  }, [onDone])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) return
    setError('')
    setLoading(true)
    let err: string | null
    if (mode === 'register') {
      if (!name) { setLoading(false); return }
      err = await register(email, name, password, accountType === 'enterprise' ? 'enterprise' : 'free')
    } else {
      err = await login(email, password)
    }
    setLoading(false)
    if (err) setError(typeof err === 'string' ? err : 'Something went wrong')
    else onDone()
  }

  const handleSsoLookup = async () => {
    if (!ssoEmail || !ssoEmail.includes('@')) { setSsoError('Enter a valid email'); return }
    const domain = ssoEmail.split('@')[1]
    try {
      const res = await fetch(`${API}/api/enterprise/sso/lookup?domain=${encodeURIComponent(domain)}`)
      const data = await res.json()
      if (data.found) {
        window.location.href = `${API}/api/enterprise/sso/login?domain=${encodeURIComponent(domain)}&email=${encodeURIComponent(ssoEmail)}`
      } else {
        setSsoError(`No enterprise SSO found for @${domain}. Contact your IT admin.`)
      }
    } catch {
      setSsoError('Failed to look up organization')
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    containerRef.current?.style.setProperty('--mx', `${e.clientX}px`)
    containerRef.current?.style.setProperty('--my', `${e.clientY}px`)
  }

  return (
    <div className="app-bg" ref={containerRef} onPointerMove={onPointerMove}>
      {/* Same background as landing page */}
      <div className="app-bg-gradient">
        <div className="app-bg-gradient-top" />
        <div className="app-bg-gradient-bottom" />
      </div>

      {/* Same cursor spotlight */}
      <div className="cursor-spot" />

      {/* PULSE watermark - vertical left side */}
      <div style={{
        position: 'fixed', left: 40, top: 0, bottom: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none', zIndex: 2,
      }}>
        <div style={{ transform: 'rotate(-90deg)', whiteSpace: 'nowrap' }}>
          <svg viewBox="0 0 900 200" width="500" height="120" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
            <defs>
              <linearGradient id="authGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#f79708" />
                <stop offset="40%" stopColor="#ce2432" />
                <stop offset="70%" stopColor="#5fd4a4" />
                <stop offset="100%" stopColor="#f5a157" />
              </linearGradient>
            </defs>
            <text x="450" y="140" textAnchor="middle" dominantBaseline="middle"
              fontFamily="ui-sans-serif, system-ui, sans-serif" fontWeight="900" fontSize="170"
              letterSpacing="6" fill="none" stroke="rgba(164, 245, 110, 0.07)" strokeWidth="1">PULSE</text>
            <text className="pulse-draw-light" x="450" y="140" textAnchor="middle" dominantBaseline="middle"
              fontFamily="ui-sans-serif, system-ui, sans-serif" fontWeight="900" fontSize="170"
              letterSpacing="6" fill="none" stroke="url(#authGrad)" strokeWidth="1.6">PULSE</text>
          </svg>
        </div>
      </div>

      {/* Nav */}
      <nav className="top-nav">
        <div className="nav-logo">
          {onBack && (
            <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 12, marginRight: 12 }}>
              ← Back
            </button>
          )}
          <div className="nav-logo-mark">P</div>
          <span className="nav-logo-text">PULSE</span>
          <span className="nav-badge">BETA</span>
        </div>
        <div />
        <button className="nav-signin" onClick={() => setMode(mode === 'login' ? 'register' : 'login')}>
          {mode === 'login' ? 'Sign Up' : 'Sign In'}
        </button>
      </nav>

      {/* Auth card */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>
        <div style={{
          position: 'relative', zIndex: 5,
          borderRadius: 20, padding: 2,
          background: 'conic-gradient(from var(--glow-angle, 0deg), #f79708, #ce2432, #7c6ef5, #5fd4a4, #f5a157, #f79708)',
          animation: 'border-glow-rotate 4s linear infinite',
        }}>
          {/* Outer blur glow */}
          <div style={{
            position: 'absolute', inset: -4, borderRadius: 24,
            background: 'conic-gradient(from var(--glow-angle, 0deg), #f79708, #ce2432, #7c6ef5, #5fd4a4, #f5a157, #f79708)',
            animation: 'border-glow-rotate 4s linear infinite',
            filter: 'blur(14px)', opacity: 0.3, zIndex: -1,
          }} />
        <div style={{
          background: 'rgba(0, 0, 0, 0.92)',
          backdropFilter: 'blur(24px)',
          borderRadius: 18, padding: '40px 36px',
          width: 400, maxWidth: '92vw',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          animation: 'lp2-fadeUp 0.6s ease-out',
        }}>


          <h2 style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif', fontWeight: 900, fontSize: 22, color: '#fff', textAlign: 'center', marginTop: 8 }}>
            {mode === 'login' ? 'Welcome back' : 'Create your account'}
          </h2>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', fontFamily: 'ui-monospace, monospace', textAlign: 'center', marginBottom: 16 }}>
            {mode === 'login' ? 'Sign in to your AI coding environment' : 'Start building with the AI IDE that beats everything'}
          </p>

          {error && (
            <div style={{ color: '#f07070', fontSize: 11, fontFamily: 'ui-monospace, monospace', textAlign: 'center', width: '100%', padding: 6, borderRadius: 6, background: 'rgba(240,112,112,0.08)', border: '1px solid rgba(240,112,112,0.2)' }}>
              {error}
            </div>
          )}

          {/* OAuth */}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 4 }}>
            <a href={`${API}/api/auth/github`} style={oauthStyle}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
              Continue with GitHub
            </a>
            <a href={`${API}/api/auth/google`} style={{ ...oauthStyle, borderColor: 'rgba(66,133,244,0.25)', background: 'rgba(66,133,244,0.06)' }}>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continue with Google
            </a>
          </div>

          {/* Divider */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, margin: '14px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>or sign in with email</span>
            <div style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {mode === 'register' && (
              <>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setAccountType('individual')} style={accountType === 'individual' ? typeActiveStyle : typeStyle}>
                    <span style={{ fontSize: 18 }}>👤</span>
                    <span style={{ fontSize: 10 }}>Individual</span>
                  </button>
                  <button type="button" onClick={() => setAccountType('enterprise')} style={accountType === 'enterprise' ? typeActiveStyle : typeStyle}>
                    <span style={{ fontSize: 18 }}>🏢</span>
                    <span style={{ fontSize: 10 }}>Enterprise</span>
                  </button>
                </div>
                <input style={inputStyle} type="text" placeholder={accountType === 'enterprise' ? 'Company name' : 'Full name'} value={name} onChange={e => setName(e.target.value)} />
              </>
            )}
            <input style={inputStyle} type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} />
            <input style={inputStyle} type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} />
            <button type="submit" disabled={loading} style={submitStyle}>
              {loading ? '...' : mode === 'login' ? '⚡ Sign In' : '⚡ Create Account'}
            </button>
          </form>

          {/* Switch */}
          <div style={{ marginTop: 16, fontSize: 11, color: 'rgba(255,255,255,0.35)', fontFamily: 'ui-monospace, monospace' }}>
            {mode === 'login' ? (
              <>Don't have an account? <button onClick={() => { setMode('register'); setError('') }} style={switchBtnStyle}>Sign up</button></>
            ) : (
              <>Already have an account? <button onClick={() => { setMode('login'); setError('') }} style={switchBtnStyle}>Sign in</button></>
            )}
          </div>

          <button onClick={() => { setSsoEmail(''); setSsoMode(true) }} style={{ marginTop: 10, background: 'none', border: 'none', color: 'rgba(95,212,164,0.6)', cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontSize: 10 }}>
            🔒 Enterprise SSO Login
          </button>
        </div>
        </div>
      </div>

      {/* SSO Modal */}
      {ssoMode && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSsoMode(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: 380, maxWidth: '90vw', background: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,208,134,0.15)', borderRadius: 16, padding: 28 }}>
            <h3 style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 4 }}>Enterprise SSO</h3>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: 'ui-monospace, monospace', marginBottom: 14 }}>Enter your work email to find your organization</p>
            <input type="email" placeholder="you@company.com" value={ssoEmail} onChange={e => { setSsoEmail(e.target.value); setSsoError('') }} onKeyDown={e => { if (e.key === 'Enter') handleSsoLookup() }} style={{ ...inputStyle, marginBottom: 10 }} autoFocus />
            {ssoError && <div style={{ color: '#f07070', fontSize: 10, fontFamily: 'ui-monospace, monospace', marginBottom: 8 }}>{ssoError}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setSsoMode(false)} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>Cancel</button>
              <button onClick={handleSsoLookup} style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: '#5fd4a4', color: '#000', cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontSize: 11, fontWeight: 700 }}>Continue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const oauthStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
  height: 44, borderRadius: 10, textDecoration: 'none',
  fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 600,
  cursor: 'pointer', transition: 'all 0.2s',
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff',
}

const inputStyle: React.CSSProperties = {
  width: '100%', height: 42, borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(255,255,255,0.03)',
  color: '#fff', fontFamily: 'ui-monospace, monospace', fontSize: 12,
  padding: '0 14px', outline: 'none',
}

const submitStyle: React.CSSProperties = {
  width: '100%', height: 44, borderRadius: 10, border: 'none',
  background: 'linear-gradient(135deg, rgba(255,208,134,0.9), rgba(206,36,50,0.8))',
  color: '#fff', fontWeight: 700, fontSize: 13, letterSpacing: '0.04em',
  cursor: 'pointer', marginTop: 4,
}

const typeStyle: React.CSSProperties = {
  flex: 1, padding: 12, borderRadius: 10, cursor: 'pointer',
  border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)',
  color: 'rgba(255,255,255,0.5)', fontFamily: 'ui-monospace, monospace', fontSize: 11, fontWeight: 600,
  textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
}

const typeActiveStyle: React.CSSProperties = {
  ...typeStyle,
  borderColor: 'rgba(255,208,134,0.4)', background: 'rgba(255,208,134,0.06)', color: '#fff',
}

const switchBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: 'rgba(255,208,134,0.8)',
  cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontSize: 11,
}
